'use client';

import {clsx} from '@gfiber/typesmith';
import {Button as BaseButton} from '@base-ui/react/button';
import type React from 'react';
import {
  type MouseEventHandler,
  type ReactNode,
  useCallback,
  useRef,
} from 'react';
import 'material-icons/iconfont/material-icons.css';
import {removeFocus} from '../../../utils/focus.util';
import {Ripple, type RippleRef} from '../../Ripple/Ripple';
import styles from './IconButton.module.scss';

export type IconButtonSize = 'small' | 'medium' | 'large';

export interface IconButtonProps<T extends HTMLElement = HTMLButtonElement> {
  /** Optional ID for the icon button */
  id?: string;
  /** Icon content (Material Icon name or custom React node) */
  icon: string | ReactNode;
  /** Size of the icon button */
  size?: IconButtonSize;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** The type attribute of the button */
  type?: 'button' | 'submit' | 'reset';
  /** Optional href for anchor tag support */
  href?: string;
  /** Optional tag to override the button element (e.g., 'a', NextLink) */
  tag?: React.ElementType;
  /** Additional CSS classes */
  className?: string;
  /** Container wrapper className */
  containerClassName?: string;
  /** Click handler */
  onClick?: MouseEventHandler<T>;
  /** Test ID for testing */
  'data-testid'?: string;
  /** ARIA label for accessibility */
  'aria-label': string;
  /** Style prop for custom inline styles */
  style?: React.CSSProperties;
  /** Other props */
  [key: string]: unknown;
}

/**
 * Get CSS classes based on icon button size and state
 */
function getIconButtonClasses({
  size = 'medium',
  disabled,
  className,
}: Pick<
  IconButtonProps<HTMLElement>,
  'size' | 'disabled' | 'className'
>): string {
  return clsx(
    styles.iconButton,
    styles[size],
    {
      [styles.disabled]: disabled,
    },
    className,
  );
}

/**
 * v2 IconButton component
 *
 * A simple icon button component with ripple effects and size variants.
 */
export const IconButton = <T extends HTMLElement = HTMLButtonElement>({
  id,
  icon,
  size = 'medium',
  disabled = false,
  type = 'button',
  href,
  tag: Tag,
  className,
  containerClassName,
  onClick,
  'data-testid': dataTestId,
  'aria-label': ariaLabel,
  style,
  ...rest
}: IconButtonProps<T>): React.JSX.Element => {
  const containerRef = useRef<HTMLSpanElement | null>(null);
  const rippleRef = useRef<RippleRef>(null);

  const handleClick = useCallback(
    (e: React.MouseEvent<T>) => {
      removeFocus(containerRef, onClick as MouseEventHandler<HTMLElement>, e);
      rippleRef.current?.trigger(e);
    },
    [onClick],
  );

  const iconButtonClasses = getIconButtonClasses({
    size,
    disabled,
    className,
  });

  // Render Material Icon or custom icon
  const iconContent =
    typeof icon === 'string' ? (
      <span className="material-icons">{icon}</span>
    ) : (
      icon
    );

  // If href or Tag is provided, we don't use BaseButton which is strictly for <button>
  const Component = Tag || (href ? 'a' : BaseButton);

  return (
    <span
      ref={containerRef}
      className={containerClassName}
    >
      <Component
        id={id}
        href={href}
        type={Component === BaseButton ? type : undefined}
        disabled={disabled}
        className={iconButtonClasses}
        onClick={handleClick}
        data-testid={dataTestId}
        aria-label={ariaLabel}
        style={style}
        {...rest}
      >
        <Ripple ref={rippleRef} />
        {iconContent && (
          <span className={styles.iconContent}>{iconContent}</span>
        )}
      </Component>
    </span>
  );
};

IconButton.displayName = 'IconButtonV2';
